import { useMutation, UseMutationResult, useQuery, UseQueryResult } from 'react-query'

import { apiClients, makeClientQueryFn } from '@percept/hooks'

import qs from 'query-string'

import { saveAs } from 'file-saver'

import { filterTruthyMapping, formatUTC } from '@percept/utils'

import { Dictionary, TimeseriesGranularity, ReportProviderV2 } from '@percept/types'

// We want to exclude internal users from these requests apart from on dev workloads
// where we will only have internal users.
const EXCLUDE_INTERNAL_USERS = process.env.WORKLOAD_TYPE !== 'DEV'

export type UserEventType = 'PAGE_VIEW'

export type UserEventSegmentation = (
  | 'DATE'
  | 'MARKET'
  | 'PRIMARY_APPLICATION'
  | 'SECONDARY_APPLICATION'
  | 'PROVIDER'
)

export type PrimaryApplication = (
  | 'MEDIA_INVESTMENT'
  | 'MEDIA_QUALITY_DASHBOARD'
  | 'VODAFONE_MEDIA_WIZARD'
)

export type SecondaryApplication = (
  | 'ANALYTICS'
  | 'BENCHMARKING'
  | 'BRAND_GROWTH_PLANNER'
  | 'CAMPAIGN_SETUP'
  | 'CHART_BUILDER'
  | 'COMPETITIVE_INVESTMENT_SOS'
  | 'COMPETITIVE_INVESTMENT_SOV'
  | 'CUSTOM_PERFORMANCE_REPORT'
  | 'DATA_WAREHOUSE'
  | 'HOME'
  | 'INSIGHTS'
  | 'MEDIA_ACADEMY'
  | 'MEDIA_ACADEMY_ADMIN'
  | 'MEDIA_MIX_MODELLING'
  | 'MEDIA_QUALITY_RATE'
  | 'NEWS'
  | 'OPTIMISATION'
  | 'OVERVIEW'
  | 'PARTNERSHIPS'
  | 'PATHWAYS'
  | 'PERFORMANCE_OVERVIEW'
  | 'PRIMARY_INVESTMENT'
  | 'REPORT_DASHBOARD'
  | 'REPORTING'
  | 'SMART_CAMPAIGN_ASSESSMENT'
  | 'SPONSORSHIPS'
  | 'STRATEGY'
  | 'SUBMISSION_TRACKER'
  | 'USER_MANAGEMENT'
  | 'WASTAGE_TREND_REPORTS'
  | 'WEB_FORM'
  | 'WIZARD_HOME'
  | 'WIZARD_OVERVIEW'
)


export type AggregatedUserEvent = {
  event_type: UserEventType
  primary_application: PrimaryApplication
  secondary_application: SecondaryApplication | null
  date: string
  event_count: number
  user_count: number
}

export type AggregatedMarketUserEvent = AggregatedUserEvent & {
  market: string
}


type UserEventHookPropsBase = {
  start?: Date | null
  end?: Date | null
}


const getUserEventParamsBase = (
  { start, end }: UserEventHookPropsBase
): Partial<Record<'start_date' | 'end_date', string>> => {
  const params: Dictionary = {}
  const dateFormatString = 'yyyy-MM-dd'
  if( start ){
    params.start_date = formatUTC(start, dateFormatString) + 'T00:00:00'
  }
  if( end ){
    params.end_date = formatUTC(end, dateFormatString) + 'T23:59:59'
  }
  return params
}

type TenantMarketUserEventHookProps = (
  UserEventHookPropsBase & {
    tenant_id: string | null
    segments?: UserEventSegmentation[]
    granularity?: TimeseriesGranularity
    market_slugs?: string[] | null
    primary_applications?: PrimaryApplication[] | null
    secondary_applications?: SecondaryApplication[] | null
  }
)


export const useTenantMarketUserEvents = ({
  tenant_id,
  start,
  end,
  segments,
  granularity,
  market_slugs,
  primary_applications,
  secondary_applications,
}: TenantMarketUserEventHookProps): UseQueryResult<AggregatedMarketUserEvent[]> => {
  const { start_date, end_date } = getUserEventParamsBase({ start, end })
  return useQuery({
    queryKey: [
      'userEvents',
      'tenant',
      tenant_id,
      'market',
      start_date,
      end_date,
      granularity,
      market_slugs,
      primary_applications,
      secondary_applications,
      ...(segments || []).slice().sort(),
    ],
    queryFn: makeClientQueryFn<AggregatedMarketUserEvent[]>(
      apiClients.core,
      {
        url: `/user-events/tenants/${tenant_id}/market`,
        params: {
          start_date,
          end_date,
          segments,
          ...filterTruthyMapping({
            granularity,
            market_slugs,
            primary_applications,
            secondary_applications,
          }),
        },
        paramsSerializer: qs.stringify,
      }
    ),
    enabled: !!tenant_id,
    retry: false,
    refetchOnWindowFocus: false,
  })
}


export type OrgUserEventHookProps = (
  UserEventHookPropsBase & {
    org_unit_id: string | null
    user_ids?: string[] | null
    event_types?: string[] | null
    provider_filter?: ReportProviderV2[] | null
    exclude_internal_users?: boolean
    include_root_org_unit?: boolean
    include_non_report_series_views?: boolean
    segmentation?: UserEventSegmentation[] | null
    limit_bottom_n_total_activity?: number | null
    limit_top_n_total_activity?: number | null 
  }
)
    
export type UserEventsPerOrgUnitBase = {
  org_unit_id: string
  org_unit_name: string
  provider: ReportProviderV2 | null
  primary_application?: string | null
  secondary_application?: string | null
}

export type AggregatedUserEventsPerOrgUnit = (
  UserEventsPerOrgUnitBase & {
    total_user_events: number | null
    total_unique_users: number | null
  }
)

export const useOrgUserEventsTotal = ({
  org_unit_id,
  start,
  end,
  exclude_internal_users = EXCLUDE_INTERNAL_USERS,
  include_non_report_series_views,
  include_root_org_unit = false,
  segmentation = null,
  user_ids,
  event_types,
  provider_filter,
  limit_bottom_n_total_activity,
  limit_top_n_total_activity,
}: OrgUserEventHookProps): UseQueryResult<AggregatedUserEventsPerOrgUnit[]> => {
  const { start_date, end_date } = getUserEventParamsBase({ start, end })
  return useQuery({
    queryKey: [
      'userEvents',
      'orgUnits',
      org_unit_id,
      'aggregatedUserEventsByChildOrg',
      'total',
      start_date,
      end_date,
      user_ids,
      event_types,
      provider_filter,
      include_root_org_unit,
      exclude_internal_users,
      include_non_report_series_views,
      segmentation,
      limit_top_n_total_activity,
      limit_bottom_n_total_activity,
    ],
    queryFn: makeClientQueryFn<AggregatedUserEventsPerOrgUnit[]>(
      apiClients.core,
      {
        url: `/user-events/org-units/${org_unit_id}/aggregated-user-events-by-child-org/total`,
        params: {
          start_date,
          end_date,
          ...filterTruthyMapping({
            user_ids,
            event_types,
            provider_filter,
            include_root_org_unit,
            exclude_internal_users,
            include_non_report_series_views,
            segmentation,
            limit_top_n_total_activity,
            limit_bottom_n_total_activity,
          }),
        },
        paramsSerializer: qs.stringify,
      }
    ),
    enabled: !!(org_unit_id && start_date && end_date),
    retry: false,
    refetchOnWindowFocus: false,
  })
}

export type PeriodUserEventsPerOrgUnit = {
  total_user_events: number
  total_unique_users: number
  start_date: string
  end_date: string
}

export type TimeseriesUserEventsPerOrgUnit = UserEventsPerOrgUnitBase & {
  timeseries: PeriodUserEventsPerOrgUnit[]
}

export type TimeseriesOrgUserEventHookProps = (
  OrgUserEventHookProps & {
    granularity: string | null
  }
)

export const useOrgUserEventsTimeseries = ({
  org_unit_id,
  start,
  end,
  granularity,
  user_ids,
  event_types,
  provider_filter,
  segmentation = null,
  include_root_org_unit = false,
  exclude_internal_users = EXCLUDE_INTERNAL_USERS,
  include_non_report_series_views,
  limit_bottom_n_total_activity,
  limit_top_n_total_activity,
}: TimeseriesOrgUserEventHookProps): UseQueryResult<TimeseriesUserEventsPerOrgUnit[]> => {
  const { start_date, end_date } = getUserEventParamsBase({ start, end })
  return useQuery({
    queryKey: [
      'userEvents',
      'orgUnits',
      org_unit_id,
      'aggregatedUserEventsByChildOrg',
      'timeseries',
      start_date,
      end_date,
      granularity,
      user_ids,
      event_types,
      provider_filter,
      include_root_org_unit,
      exclude_internal_users,
      include_non_report_series_views,
      segmentation,
      limit_top_n_total_activity,
      limit_bottom_n_total_activity,
    ],
    queryFn: makeClientQueryFn<TimeseriesUserEventsPerOrgUnit[]>(
      apiClients.core,
      {
        url: `/user-events/org-units/${org_unit_id}/aggregated-user-events-by-child-org/timeseries`,
        params: {
          start_date,
          end_date,
          ...filterTruthyMapping({
            granularity,
            user_ids,
            event_types,
            provider_filter,
            include_root_org_unit,
            exclude_internal_users,
            include_non_report_series_views,
            segmentation,
            limit_top_n_total_activity,
            limit_bottom_n_total_activity,
          }),
        },
        paramsSerializer: qs.stringify,
      }
    ),
    enabled: !!(org_unit_id && start_date && end_date),
    retry: false,
    refetchOnWindowFocus: false,
  })
}


type TenantMarketUserEventExportHookProps = (
  Omit<TenantMarketUserEventHookProps, 'segments'> & {
    cardinality_type: 'VIEWS' | 'USERS'
  }
)


export const useTenantMarketUserEventsCSVExport = (): UseMutationResult<
  void, unknown, TenantMarketUserEventExportHookProps
> => {
  return useMutation({
    mutationFn: async ({
      tenant_id,
      cardinality_type,
      start,
      end,
      market_slugs,
      primary_applications,
      secondary_applications,
    }: TenantMarketUserEventExportHookProps) => {
      const response = await apiClients.core.get<Blob>(
        `/user-events/tenants/${tenant_id}/market/export`,
        {
          params: {
            cardinality_type: cardinality_type.toLowerCase(),
            ...getUserEventParamsBase({ start, end }),
            ...filterTruthyMapping({
              market_slugs,
              primary_applications,
              secondary_applications,
            })
          },
          paramsSerializer: qs.stringify,
          responseType: 'blob',
          headers: {
            'Content-Type': 'text/csv',
          }
        }
      )

      // Get filename from content disposition header if available
      const contentDisposition: string = response.headers && response.headers['content-disposition']
      // Set default filename if not found in response headers
      let filename = 'user_events.csv'
      if( contentDisposition ){
        const headerValues = contentDisposition.split(';').map( s => s.trim())
        const filenameValue = headerValues.find( v => v.startsWith('filename='))
        if( filenameValue ){
          filename = filenameValue.replace('filename=', '')
        }
      }
      saveAs(response.data, filename)
    }
  })
}
